import React from "react";
import { motion } from "framer-motion";

const Loader = ({
  text = "Please wait... system loading",
  variant = "dots",
  size = "md"
}) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
  };

  const dotSize = {
    sm: "w-2 h-2",
    md: "w-3 h-3",
    lg: "w-4 h-4",
  };

  // Spinner variant
  if (variant === "spinner") {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <motion.div
          className={`${sizeClasses[size]} border-4 border-primary-200 border-t-primary-600 rounded-full`}
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
        {text && (
          <p className="mt-4 text-neutral-600 text-sm font-medium animate-pulse">
            {text}
          </p>
        )}
      </div>
    );
  }

  // Bouncing dots variant (default)
  if (variant === "dots") {
    const containerVariants = {
      start: {
        transition: {
          staggerChildren: 0.2,
        },
      },
      end: {
        transition: {
          staggerChildren: 0.2,
        },
      },
    };

    const dotVariants = {
      start: {
        y: "0%",
      },
      end: {
        y: "100%",
      },
    };

    const dotTransition = {
      duration: 0.5,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut",
    };

    return (
      <div className="flex flex-col items-center justify-center py-8">
        <motion.div
          className="flex gap-2"
          variants={containerVariants}
          initial="start"
          animate="end"
        >
          <motion.div
            className={`${dotSize[size]} bg-primary-600 rounded-full`}
            variants={dotVariants}
            transition={dotTransition}
          />
          <motion.div
            className={`${dotSize[size]} bg-primary-500 rounded-full`}
            variants={dotVariants}
            transition={dotTransition}
          />
          <motion.div
            className={`${dotSize[size]} bg-primary-400 rounded-full`}
            variants={dotVariants}
            transition={dotTransition}
          />
        </motion.div>
        {text && (
          <motion.p
            className="mt-4 text-neutral-600 text-sm font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {text}
          </motion.p>
        )}
      </div>
    );
  }

  // Pulse variant
  if (variant === "pulse") {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <motion.div
          className={`${sizeClasses[size]} bg-primary-600 rounded-full`}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [1, 0.5, 1],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        {text && (
          <p className="mt-4 text-neutral-600 text-sm font-medium animate-pulse">
            {text}
          </p>
        )}
      </div>
    );
  }

  // Progress bar variant
  if (variant === "progress") {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-8 max-w-md mx-auto">
        <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
            initial={{ x: "-100%" }}
            animate={{ x: "100%" }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{ width: "50%" }}
          />
        </div>
        {text && (
          <p className="mt-4 text-neutral-600 text-sm font-medium text-center">
            {text}
          </p>
        )}
      </div>
    );
  }

  // Orbit variant
  if (variant === "orbit") {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className={`relative ${sizeClasses[size]}`}>
          <motion.div
            className="absolute inset-0 border-4 border-primary-200 rounded-full"
          />
          <motion.div
            className="absolute top-0 left-1/2 -ml-1.5 w-3 h-3 bg-primary-600 rounded-full"
            animate={{ rotate: 360 }}
            transition={{
              duration: 1,
              repeat: Infinity,
              ease: "linear",
            }}
            style={{ transformOrigin: `50% ${parseInt(sizeClasses[size].split(' ')[0].replace('w-', '')) * 2}px` }}
          />
        </div>
        {text && (
          <p className="mt-4 text-neutral-600 text-sm font-medium animate-pulse">
            {text}
          </p>
        )}
      </div>
    );
  }

  // Default fallback
  return (
    <div className="flex flex-col items-center justify-center py-8">
      <div className={`${sizeClasses[size]} animate-spin rounded-full border-4 border-primary-200 border-t-primary-600`} />
      {text && (
        <p className="mt-4 text-neutral-600 text-sm font-medium">
          {text}
        </p>
      )}
    </div>
  );
};

export default Loader;
